import React, { ChangeEvent, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import { Minus, Plus, TrashSimple } from "@phosphor-icons/react";
import { STYLES } from "./ShoppingCartStyles";
import { CartItem } from "@ecommerce/store";
import { numberFormatter } from "@ecommerce/shared-utils";
import { AppConfigFields } from "../../types/appConfigEntities";
import { useTheme } from "../../util/themes";

const minimumPurchase = window.APP_CONFIG?.[AppConfigFields.MinimumPurchaseQuantity] || 1;

interface ShoppingCartItemProps {
    item: CartItem;
    onUpdateQuantity: (
        idProduct: string,
        idProductVariant: string,
        quantity: number,
    ) => Promise<boolean>;
    onRemoveItem: (idProduct: string, idProductVariant: string) => Promise<boolean>;
    isPending: boolean;
}

export const ShoppingCartItem: React.FC<ShoppingCartItemProps> = ({
    item,
    onUpdateQuantity,
    onRemoveItem,
    isPending,
}) => {
    const theme = useTheme();
    const effectiveMinQuantity = minimumPurchase ?? 1;
    const stock = item.stock ?? 999; // si no viene stock, nos comportamos como antes
    const isPurchasable = stock >= effectiveMinQuantity;

    const [inputValue, setInputValue] = useState<string>(item.quantity.toString());

    useEffect(() => {
        setInputValue(item.quantity.toString());
    }, [item.quantity]);

    const onChangeInputQuantity = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setInputValue(e.target.value);
    };

    const onBlurInputQuantity = async () => {
        let parsed = Number(inputValue);

        if (!inputValue || isNaN(parsed)) {
            parsed = effectiveMinQuantity;
        }

        if (!isPurchasable) {
            parsed = Math.min(effectiveMinQuantity, stock);
        } else {
            parsed = Math.max(parsed, effectiveMinQuantity);
            parsed = Math.min(parsed, stock);
        }

        const saved = await onUpdateQuantity(item.idProduct, item.idProductVariant, parsed);
        setInputValue((saved ? parsed : item.quantity).toString());
    };

    const handleIncrement = async () => {
        if (!isPurchasable) return;

        const next = Math.min(item.quantity + 1, stock);
        const saved = await onUpdateQuantity(item.idProduct, item.idProductVariant, next);
        setInputValue((saved ? next : item.quantity).toString());
    };

    const handleDecrement = async () => {
        const next = Math.max(item.quantity - 1, effectiveMinQuantity);
        const saved = await onUpdateQuantity(item.idProduct, item.idProductVariant, next);
        setInputValue((saved ? next : item.quantity).toString());
    };

    return (
        <Box sx={{ mb: 3, position: "relative" }}>
            <Box sx={{ display: "flex", gap: STYLES.spacing.itemGap }}>
                <Box
                    component="img"
                    src={item.image}
                    alt={item.name}
                    sx={{
                        width: 100,
                        height: 130,
                        objectFit: "cover",
                    }}
                />

                <Box sx={{ flexGrow: 1, maxWidth: { md: "45%", lg: "60%" } }}>
                    <Typography
                        sx={{
                            fontFamily: STYLES.fontFamily,
                            fontSize: "14px",
                            color: STYLES.colors.primary,
                            mb: 1,
                            textTransform: "uppercase",
                        }}
                    >
                        {item.name}
                    </Typography>

                    {item.attributes && item.attributes.length > 0 && (
                        <Typography
                            sx={{
                                fontFamily: STYLES.fontFamily,
                                fontSize: "12px",
                                color: "#666",
                                mb: 1,
                                textTransform: "capitalize",
                            }}
                        >
                            ({item.attributes.map((a) => a.value).join(", ")})
                        </Typography>
                    )}

                    <Typography
                        sx={{
                            fontFamily: STYLES.fontFamily,
                            fontSize: "14px",
                            color: STYLES.colors.primary,
                            fontWeight: "bold",
                            mb: 1,
                        }}
                    >
                        {numberFormatter.toStringWithAmount(item.price, "$")}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "fit-content",
                            border: `1px solid ${theme.colors.greyLight}`,
                            borderRadius: 1,
                            overflow: "hidden",
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={handleDecrement}
                            disabled={isPending || item.quantity <= effectiveMinQuantity}
                            sx={{
                                borderRight: `1px solid ${theme.colors.greyLight}`,
                                borderRadius: 0,
                                color: theme.colors.text.primary,
                            }}
                        >
                            <Minus size={16} />
                        </IconButton>

                        <TextField
                            value={inputValue}
                            disabled={isPending}
                            onChange={onChangeInputQuantity}
                            onBlur={onBlurInputQuantity}
                            type="number"
                            variant="outlined"
                            inputProps={{
                                min: effectiveMinQuantity,
                                style: {
                                    textAlign: "center",
                                },
                            }}
                            sx={{
                                width: "54px",
                                "& .MuiInputBase-input": {
                                    fontFamily: STYLES.fontFamily,
                                    fontSize: "13px",
                                    py: 0,
                                    px: 0,
                                    height: "30px",
                                    lineHeight: "30px",
                                    textAlign: "center",
                                    MozAppearance: "textfield",
                                    color: theme.colors.text.primary,
                                },
                                "& input[type=number]::-webkit-outer-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                },
                                "& input[type=number]::-webkit-inner-spin-button": {
                                    WebkitAppearance: "none",
                                    margin: 0,
                                },
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 0,
                                    "& fieldset": { border: "none" },
                                    "&:hover fieldset": { border: "none" },
                                    "&.Mui-focused fieldset": { border: "none" },
                                },
                            }}
                        />

                        <IconButton
                            size="small"
                            onClick={handleIncrement}
                            disabled={isPending || !isPurchasable || item.quantity >= stock}
                            sx={{
                                borderLeft: `1px solid ${theme.colors.greyLight}`,
                                borderRadius: 0,
                                color: theme.colors.text.primary,
                            }}
                        >
                            <Plus size={16} />
                        </IconButton>
                    </Box>

                    {minimumPurchase > 1 && (
                        <Typography
                            sx={{
                                fontFamily: STYLES.fontFamily,
                                fontSize: "10px",
                                color: "text.lighter",
                                mt: 1,
                            }}
                        >
                            Mínimo {minimumPurchase} unidad{minimumPurchase > 1 ? "es" : ""}
                        </Typography>
                    )}
                </Box>

                <IconButton
                    size="small"
                    onClick={() => onRemoveItem(item.idProduct, item.idProductVariant)}
                    disabled={isPending}
                    sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        color: theme.colors.text.primary,
                    }}
                >
                    <TrashSimple size={17} />
                </IconButton>
            </Box>
        </Box>
    );
};
